// src/components/CameraRequests/CreateRequestForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { cameraFootageRequestsApi, locationsApi } from '../../services/apiClient';
import { useThemeSettings } from '../../contexts/ThemeContext';

const CreateRequestForm = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useThemeSettings();

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  const [formData, setFormData] = useState({
    location_id: null,
    location_description: '',
    incident_datetime: new Date(),
    incident_description: '',
    reason_for_request: ''
  });

  const [errors, setErrors] = useState({});

  // Load locations (only type=1 sites with cameras)
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await locationsApi.getAll({ locationType: 1 });

        if (response.data.success) {
          // Filter only locationType = 1 (sites/buildings with cameras)
          const sitesWithCameras = response.data.data.filter(
            loc => loc.locationType === 1
          );
          setLocations(sitesWithCameras);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
        enqueueSnackbar(t ? t('CameraRequests.error_load_locations') : 'Failed to load locations', { variant: 'error' });
      } finally {
        setLoadingLocations(false);
      }
    };

    if (open) {
      loadLocations();
    }
  }, [open, enqueueSnackbar]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.location_id) {
      newErrors.location_id = t ? t('CameraRequests.validation_location_required') : 'Location is required';
    }

    if (!formData.location_description || formData.location_description.length < 10) {
      newErrors.location_description = t ? t('CameraRequests.validation_location_description') : 'Please provide a detailed location description (min 10 characters)';
    }

    if (!formData.incident_datetime) {
      newErrors.incident_datetime = t ? t('CameraRequests.validation_datetime_required') : 'Incident date and time is required';
    }

    if (formData.incident_datetime > new Date()) {
      newErrors.incident_datetime = t ? t('CameraRequests.validation_datetime_future') : 'Incident date cannot be in the future';
    }

    if (!formData.incident_description || formData.incident_description.length < 20) {
      newErrors.incident_description = t ? t('CameraRequests.validation_incident_description') : 'Please provide a detailed incident description (min 20 characters)';
    }

    if (!formData.reason_for_request || formData.reason_for_request.length < 10) {
      newErrors.reason_for_request = t ? t('CameraRequests.validation_reason_required') : 'Please provide a reason for requesting footage (min 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const response = await cameraFootageRequestsApi.create(formData);

      if (response.data.success) {
        enqueueSnackbar(t ? t('CameraRequests.success_created') : 'Camera footage request created successfully', { variant: 'success' });
        onSuccess(response.data.data);
      }
    } catch (error) {
      console.error('Error creating request:', error);
      enqueueSnackbar(
        error.response?.data?.error || (t ? t('CameraRequests.error_create') : 'Failed to create camera footage request'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5">{t ? t('CameraRequests.new_request_title') : 'New Camera Footage Request'}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t ? t('CameraRequests.new_request_subtitle') : 'Request camera footage for an incident. This request will go through Site Manager and DPO approval.'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Location Selection */}
          <Autocomplete
            options={locations}
            getOptionLabel={(option) => option.friendlyName || ''}
            loading={loadingLocations}
            value={locations.find(loc => loc.id === formData.location_id) || null}
            onChange={(event, newValue) => handleChange('location_id', newValue?.id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${t ? t('CameraRequests.location') : 'Location'} *`}
                error={!!errors.location_id}
                helperText={errors.location_id || (t ? t('CameraRequests.location_helper') : 'Select the site/building where the incident occurred')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Location Description */}
          <TextField
            label={`${t ? t('CameraRequests.detailed_location') : 'Detailed Location Description'} *`}
            multiline
            rows={2}
            value={formData.location_description}
            onChange={(e) => handleChange('location_description', e.target.value)}
            error={!!errors.location_description}
            helperText={errors.location_description || (t ? t('CameraRequests.detailed_location_helper') : 'Describe the specific area within the location (e.g., "Main entrance hallway, near elevator")')}
            placeholder={t ? t('CameraRequests.detailed_location_placeholder') : 'e.g., Main entrance hallway, near the reception desk...'}
          />

          {/* Incident DateTime */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label={`${t ? t('CameraRequests.incident_datetime') : 'Incident Date and Time'} *`}
              value={formData.incident_datetime}
              onChange={(newValue) => handleChange('incident_datetime', newValue)}
              maxDateTime={new Date()}
              ampm={false}
              slotProps={{
                textField: {
                  error: !!errors.incident_datetime,
                  helperText: errors.incident_datetime || (t ? t('CameraRequests.incident_datetime_helper') : 'When did the incident occur?')
                }
              }}
            />
          </LocalizationProvider>

          {/* Incident Description */}
          <TextField
            label={`${t ? t('CameraRequests.incident_description') : 'Incident Description'} *`}
            multiline
            rows={4}
            value={formData.incident_description}
            onChange={(e) => handleChange('incident_description', e.target.value)}
            error={!!errors.incident_description}
            helperText={errors.incident_description || (t ? t('CameraRequests.incident_description_helper') : 'Provide a detailed description of what happened')}
            placeholder={t ? t('CameraRequests.incident_description_placeholder') : 'Describe the incident in detail...'}
          />

          {/* Reason for Request */}
          <TextField
            label={`${t ? t('CameraRequests.reason_for_request') : 'Reason for Requesting Footage'} *`}
            multiline
            rows={3}
            value={formData.reason_for_request}
            onChange={(e) => handleChange('reason_for_request', e.target.value)}
            error={!!errors.reason_for_request}
            helperText={errors.reason_for_request || (t ? t('CameraRequests.reason_helper') : 'Explain why you need to access this footage')}
            placeholder={t ? t('CameraRequests.reason_placeholder') : 'e.g., Security incident investigation, insurance claim, etc...'}
          />

          <Alert severity="info">
            <strong>{t ? t('CameraRequests.gdpr_notice') : 'GDPR Notice:'}</strong> {t ? t('CameraRequests.gdpr_notice_text') : 'This request will be reviewed by the Site Manager and Data Protection Officer (DPO) to ensure compliance with data protection regulations. Footage will only be provided if approved by both parties.'}
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t ? t('Common.cancel') : 'Cancel'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {t ? t('CameraRequests.submit_request') : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRequestForm;
